import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import type { Coverage } from "@/src/models/coverage";

const ORIGINAL_MEDICARE_PAYER_ID = "03102";
const MEDICARE_WRAP_PATTERN = /\bmedicare\s+wrap\b/i;
const MEDICARE_ADVANTAGE_PATTERN =
  /\bmedicare\b.*\b(?:adv(?:antage)?|replacement|risk)\b|\b(?:adv(?:antage)?|replacement|risk)\b.*\bmedicare\b/i;
const MANAGED_MEDICARE_PATTERN = /\b(?:hmo|ppo|pos|pffs)\b/i;

export function tprEligibilityProductText(
  eligibility:
    | {
        skipped: boolean;
        checkFailed: boolean;
        eligibilityStatus: string | null;
        planType?: string | null;
        planName?: string | null;
      }
    | null
    | undefined
): string {
  if (!eligibility || eligibility.skipped || eligibility.checkFailed || eligibility.eligibilityStatus !== "active") {
    return "";
  }

  return [eligibility.planType, eligibility.planName].filter(Boolean).join(" ");
}

function isOriginalMedicareCoverage(coverage: Coverage): boolean {
  const coverageName = [coverage.payer, coverage.plan].filter(Boolean).join(" ");

  return (
    coverage.customerPayer?.trim() === ORIGINAL_MEDICARE_PAYER_ID ||
    /^medicare(?:\s+part\s+[ab])?$/i.test(coverage.payer?.trim() ?? "") ||
    MEDICARE_WRAP_PATTERN.test(coverageName)
  );
}

function isMedicareAdvantageCoverage(coverage: Coverage, eligibilityProductText: string): boolean {
  const combinedText = [coverage.payer, coverage.plan, coverage.lineOfBusiness, eligibilityProductText]
    .filter(Boolean)
    .join(" ");

  return (
    MEDICARE_ADVANTAGE_PATTERN.test(combinedText) ||
    (MANAGED_MEDICARE_PATTERN.test(combinedText) && /\bmedicare\b/i.test(combinedText)) ||
    /\bdual\s+(?:eligible|complete)\b/i.test(combinedText)
  );
}

function isMedicareSupplementCoverage(coverage: Coverage): boolean {
  const text = [coverage.payer, coverage.plan, coverage.lineOfBusiness].filter(Boolean).join(" ");

  return /\b(?:supplement|medigap|senior security|plan [a-n]|aarp(?!\s*medicare advantage))\b/i.test(text);
}

export function isTricareForLifeCoverage(coverage: Pick<Coverage, "payer" | "plan">): boolean {
  const text = [coverage.payer, coverage.plan].filter(Boolean).join(" ");
  const hasForLifeName = /\btricare\s+for\s+life\b/i.test(text);
  const hasTricareTfl = /\btricare\b/i.test(coverage.payer ?? "") && /\btfl\b/i.test(text);

  return (hasForLifeName || hasTricareTfl) && !/\b(?:tri\s*west|prime|select)\b/i.test(text);
}

function isMedicaidOrPayerOfLastResort(coverage: Coverage): boolean {
  const text = [coverage.payer, coverage.plan, coverage.customerPayer, coverage.lineOfBusiness]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (/\bdual\s+(?:eligible|complete)\b/i.test(text) || /\bmedicare\s+replacement\b/i.test(text)) return false;

  return /\b(?:ahcccs|medicaid|mercy care|health choice|care1st|banner university family|indian health|ihs|pimc|sliding fee|chip)\b/i.test(
    text
  );
}

function getCoverageCobTier(coverage: Coverage): number {
  if (isMedicaidOrPayerOfLastResort(coverage)) return 3;

  if (isMedicareSupplementCoverage(coverage) || isTricareForLifeCoverage(coverage)) return 2;

  return 1;
}

export function selectTprAthenaCoverages(coverages: Coverage[], eligibilityProductText = ""): Coverage[] {
  if (coverages.length <= 1) return coverages;

  const isMedicareAdvantage = (coverage: Coverage) =>
    isMedicareAdvantageCoverage(coverage, coverage.order === 1 ? eligibilityProductText : "");
  const selected = coverages.some(isMedicareAdvantage)
    ? coverages.filter(
        (coverage) =>
          isMedicareAdvantage(coverage) ||
          (!isOriginalMedicareCoverage(coverage) && !isMedicareSupplementCoverage(coverage))
      )
    : coverages;

  if (selected.length <= 1) return selected.map((coverage, index) => ({ ...coverage, order: index + 1 }));

  return [...selected]
    .sort((a, b) => {
      const tierDelta = getCoverageCobTier(a) - getCoverageCobTier(b);

      if (tierDelta !== 0) return tierDelta;

      const aIsSelf =
        a.relationshipToSubscriber == null || a.relationshipToSubscriber === RelationshipToSubscriber.SELF;
      const bIsSelf =
        b.relationshipToSubscriber == null || b.relationshipToSubscriber === RelationshipToSubscriber.SELF;

      if (aIsSelf !== bIsSelf) return aIsSelf ? -1 : 1;

      return (a.order ?? 0) - (b.order ?? 0);
    })
    .map((coverage, index) => ({ ...coverage, order: index + 1 }));
}
